import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import {
  Observable,
  Subject,
  catchError,
  forkJoin,
  map,
  of,
  retry,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ExternalFileId } from '../connect/models/external-file-id';
import { DialogComponent } from '../dialog/dialog.component';
import { DialogData } from '../dialog/dialog.model';
import { DialogService } from '../dialog/dialog.service';
import { HANDLE_ERROR } from '../error-handling/error-handler-operator';
import { ClearError } from '../error-handling/error.actions';
import { LoadingService } from '../loading/loading.service';
import { PageData } from '../shared/page-data.model';
import { GET_SCAN_PROJECT_URL } from '../utils/get-scan-project-url';
import {
  getPageInfoQueryString,
  getScandataQuerystring,
  getSortInfoQueryString,
} from './scandata-query';
import { ScandataQuery } from './scandata-query.models';
import {
  AddScans,
  ClearScandata,
  PatchScandataModel,
  PatchScandataModels,
  SetIsLoading,
  SetScandata,
  UpdateScandata,
} from './scandata.actions';
import {
  PointcloudStatus,
  PointcloudStatusMap,
  PointcloudType,
  ScandataDisplayStatus,
  ScandataModel,
  UpdateScandataModel,
  UpdateScandataModelResult,
} from './scandata.models';
import { ScandataState } from './scandata.state';

@Injectable({
  providedIn: 'root',
})
export class ScandataService {
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private readonly handleError = inject(HANDLE_ERROR);

  private readonly cancelGetScandata = new Subject<void>();
  private retryDialogRef: MatDialogRef<DialogComponent> | null = null;

  constructor(
    private store: Store,
    private http: HttpClient,
    private loadingService: LoadingService,
    private dialogService: DialogService,
  ) {
    this.loadingService
      .isLoading$(this)
      .pipe(tap((loading) => this.store.dispatch(new SetIsLoading(loading))))
      .subscribe();
  }

  setDisplayStatus(scandata: ScandataModel[], displayStatus: ScandataDisplayStatus) {
    if (scandata.length === 0) {
      return;
    }

    const patchModels: Partial<ScandataModel>[] = scandata.map((item) => ({
      id: item.id,
      displayStatus,
    }));

    this.store.dispatch(new PatchScandataModels(patchModels));
  }

  loadScandata() {
    return this.getScandata().pipe(
      switchMap((data) => {
        /**
         * This could complete after scans have been set in the store by the 3D host,
         * therefore it dispatches UpdateScandata, and not SetScandata.
         */
        return this.store.dispatch(new UpdateScandata(data));
      }),
    );
  }

  refreshScandata() {
    return this.getScandata().pipe(
      switchMap((data) => {
        return this.store.dispatch(new SetScandata(data));
      }),
    );
  }

  getScandataModel(pointcloudId: string): Observable<ScandataModel> {
    return this.store.selectOnce(ScandataState.scandata).pipe(
      map((models) => {
        return models.find((model) => model.id == pointcloudId);
      }),
      switchMap((model) =>
        isDefined(model)
          ? of(model)
          : this.getAndCacheScandataModel(pointcloudId).pipe(
              this.handleError('scanDetailsLoadError'),
            ),
      ),
    );
  }

  getScanByExternalFileId(externalFileId: ExternalFileId, useOnlyHash = false) {
    // Filter for latest format where externalFileId starts with fileId and contains hash
    // This uses Gridify syntax
    const filterByList: (string | null)[] = [
      `externalFileId^${externalFileId.fileId}:,externalFileId=*:${externalFileId.hash}`,
    ];

    if (!useOnlyHash) {
      // Add previous formats to the filter list (fileId:versionId:revision and fileId)
      filterByList.push(
        `externalFileId=${externalFileId.idWithoutHash}`,
        `externalFileId=${externalFileId.fileId}`,
      );
    }

    return this.getScanUsingFilterByList(filterByList).pipe(
      map((scan) => (scan ? this.mapScandataModel(scan) : undefined)),
      switchMap((scan) => (isDefined(scan) ? this.cacheScanIfMatchesFilters(scan) : of(undefined))),
    );
  }

  getScansByQuery(query: Partial<ScandataQuery>) {
    const querystring = getScandataQuerystring(query);

    return this.getScanProjectUrl$('/pointclouds').pipe(
      switchMap((url) => this.http.get<PageData<ScandataModel>>(`${url}?${querystring}`)),
      map((pageData) => pageData.items),
      map((scans) => scans.map((scan) => this.mapScandataModel(scan))),
    );
  }

  updateScandataModel(updateScandataModel: UpdateScandataModel): Observable<ScandataModel> {
    return this.getScanProjectUrl$(`/pointclouds/${updateScandataModel.id}`).pipe(
      switchMap((url) => this.http.patch<ScandataModel>(url, updateScandataModel)),
      map((updatedModel) => this.mapScandataModel(updatedModel)),
      switchMap((updatedModel) => {
        const patchModel = {
          ...updatedModel,
          ...{
            // These properties could have been set to null and
            // will then be absent from the updateModel object.
            // So we have to explicitly set them to here.
            captureDate: updatedModel.captureDate,
            scannerType: updatedModel.scannerType,
            notes: updatedModel.notes,
          },
        };

        return this.store
          .dispatch(new PatchScandataModel(patchModel))
          .pipe(map(() => updatedModel));
      }),
      this.handleError('scanDetailsSaveError'),
    );
  }

  updateScandataModels(
    updateScandataModels: UpdateScandataModel[],
  ): Observable<UpdateScandataModelResult[]> {
    const updateList$: Observable<UpdateScandataModelResult>[] = updateScandataModels.map(
      (scan) => {
        return this.getScanProjectUrl$(`/pointclouds/${scan.id}`).pipe(
          switchMap((url) => this.http.patch<ScandataModel>(url, scan)),
          map((responseScandataModel) => ({ updateScandataModel: scan, responseScandataModel })),
          catchError((error) => of({ updateScandataModel: scan, error })),
        );
      },
    );

    return forkJoin(updateList$).pipe(
      switchMap((result) => {
        const patchActions = result
          .filter((item) => isNil(item.error) && isDefined(item.responseScandataModel))
          .map((item) => item.responseScandataModel as ScandataModel)
          .map((updatedModel) => this.mapScandataModel(updatedModel))
          .map((updatedModel) => {
            const patchModel = {
              ...updatedModel,
              ...{
                // These properties could have been set to null and
                // will then be absent from the updateModel object.
                // So we have to explicitly set them to here.
                captureDate: updatedModel.captureDate,
                scannerType: updatedModel.scannerType,
                notes: updatedModel.notes,
              },
            };

            return new PatchScandataModel(patchModel);
          });

        return this.store.dispatch(patchActions).pipe(map(() => result));
      }),
    );
  }

  mapScandataModel(scandataModel: ScandataModel): ScandataModel {
    // TODO: Remove once imaging service stations are supported
    // Set numberOfStations to 0 to disable station listing and icons in 3D view
    if (scandataModel.type === PointcloudType.MobileMappingRun) {
      scandataModel.numberOfStations = 0;
    }

    this.assignScandataModelStatus(scandataModel);
    this.convertScandataModelDates(scandataModel);
    return scandataModel;
  }

  assignWeb3dId(scan: ScandataModel) {
    scan.web3dId = uuidv4();
    return scan;
  }

  private getScandata(): Observable<ScandataModel[]> {
    this.cancelGetScandata.next();

    const fetchScandata$ = this.store.selectOnce(ScandataState.query).pipe(
      tap(() => this.retryDialogRef?.close()),
      map((query) => getScandataQuerystring(query)),
      switchMap((querystring) => this.getScanProjectUrl$(`/pointclouds?${querystring}`)),
      switchMap((url) => {
        const request = this.http.get<PageData<ScandataModel>>(url);
        return this.loadingService.loadFrom(request, this);
      }),
      map((pageData) => pageData.items),
    );

    return fetchScandata$.pipe(
      this.handleError('scanLoadError'),
      tap({ error: () => this.store.dispatch(ClearScandata) }),
      retry({ delay: (error: unknown) => this.prompUserToRetry(error) }),
      map((scans) => scans.map((scan) => this.mapScandataModel(scan))),
      map((scans) => scans.map((scan) => this.assignWeb3dId(scan))),
      takeUntil(this.cancelGetScandata),
    );
  }

  private prompUserToRetry(error: unknown) {
    return this.showRetryDialog().pipe(
      switchMap((retryClicked) => {
        return retryClicked
          ? this.store.dispatch(new ClearError('scanLoadError'))
          : throwError(() => error);
      }),
    );
  }

  private getAndCacheScandataModel(pointcloudId: string) {
    return this.getScanProjectUrl$(`/pointclouds/${pointcloudId}`).pipe(
      switchMap((url) => this.http.get<ScandataModel>(url)),
      map((scan) => this.mapScandataModel(scan)),
      switchMap((scan) => this.cacheScandataModel(scan)),
    );
  }

  private cacheScandataModel(scan: ScandataModel) {
    return this.store.selectOnce(ScandataState.getScan(scan.id)).pipe(
      switchMap((storeScan) =>
        isNil(storeScan)
          ? this.store.dispatch(new AddScans([this.assignWeb3dId(scan)]))
          : this.store.dispatch(new PatchScandataModel(scan)),
      ),
      map(() => scan),
    );
  }

  private scanMatchesFilters(scan: ScandataModel): Observable<boolean> {
    return this.store.selectOnce(ScandataState.query).pipe(
      map((query) => getScandataQuerystring(query)),
      switchMap((querystring) =>
        this.getScanProjectUrl$(`/pointclouds?${querystring},pointcloudId=${scan.id}`),
      ),
      switchMap((url) => this.http.get<PageData<ScandataModel>>(url)),
      map((pageData) => pageData.items.length > 0),
    );
  }

  private cacheScanIfMatchesFilters(scan: ScandataModel) {
    if (isDefined(this.store.selectSnapshot(ScandataState.getScan(scan.id))))
      return this.store.dispatch(new PatchScandataModel(scan)).pipe(map(() => scan));

    const upsertStore = this.store.selectOnce(ScandataState.getScan(scan.id)).pipe(
      switchMap((storeScan) =>
        isNil(storeScan)
          ? this.store.dispatch(new AddScans([this.assignWeb3dId(scan)]))
          : this.store.dispatch(new PatchScandataModel(scan)),
      ),
      map(() => scan),
    );

    return this.scanMatchesFilters(scan).pipe(
      switchMap((matchesFilters) => (matchesFilters ? upsertStore : of(scan))),
    );
  }

  private assignScandataModelStatus(scandataModel: ScandataModel) {
    scandataModel.pointcloudStatus = PointcloudStatusMap.get(
      scandataModel.status,
    ) as PointcloudStatus;
  }

  private convertScandataModelDates(scandataModel: ScandataModel) {
    if (isDefined(scandataModel.captureDate))
      scandataModel.captureDate = new Date(scandataModel.captureDate);

    if (isDefined(scandataModel.uploadedDate))
      scandataModel.uploadedDate = new Date(scandataModel.uploadedDate);

    if (isDefined(scandataModel.ingestionStartedDate))
      scandataModel.ingestionStartedDate = new Date(scandataModel.ingestionStartedDate);

    if (isDefined(scandataModel.ingestionFinishedDate))
      scandataModel.ingestionFinishedDate = new Date(scandataModel.ingestionFinishedDate);

    if (isDefined(scandataModel.deletedDate))
      scandataModel.deletedDate = new Date(scandataModel.deletedDate);
  }

  private showRetryDialog() {
    const dialogData = new DialogData('Loading Error', 'Something went wrong loading your data.', {
      text: 'Retry',
      color: 'primary',
    });

    this.retryDialogRef = this.dialogService.showMessage(dialogData, { disableClose: true });

    return this.retryDialogRef.afterClosed().pipe(map((confirmed) => confirmed ?? false));
  }

  private getScanUsingFilterByList(
    filterByList: (string | null)[],
  ): Observable<ScandataModel | null> {
    const filterBy = filterByList.shift();
    if (isNil(filterBy)) return of(null);

    const sortBy = getSortInfoQueryString({ sortBy: 'uploadedDate', sortDirection: 'desc' });
    const pageInfo = getPageInfoQueryString({ pageSize: 1, pageIndex: 0 });
    const querystring = `filterBy=${filterBy}&${sortBy}&${pageInfo}`;

    return this.getScanProjectUrl$(`/pointclouds?${querystring}`).pipe(
      switchMap((url) => this.http.get<PageData<ScandataModel>>(url)),
      switchMap((pageData) =>
        pageData.items.length > 0
          ? of(pageData.items[0])
          : this.getScanUsingFilterByList(filterByList),
      ),
    );
  }
}
